import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { scheduledEmailTypeAPI } from '../services/api';

/**
 * NotificationListPage - Full management view for all scheduled notifications
 * Directly queries the ScheduledNotification collection for complete visibility
 */
const NotificationListPage = () => {
  const navigate = useNavigate();

  // Data state
  const [notifications, setNotifications] = useState([]);
  const [emailTypes, setEmailTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });

  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    emailTypeId: '',
    fromDate: '',
    toDate: '',
    search: ''
  });

  // Sort state
  const [sortBy, setSortBy] = useState('scheduledFor');
  const [sortOrder, setSortOrder] = useState('asc');

  // Edit modal state
  const [editModal, setEditModal] = useState({ open: false, notification: null });
  const [editForm, setEditForm] = useState({});

  // Status color helper
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      skipped: 'bg-gray-100 text-gray-600',
      processing: 'bg-amber-100 text-amber-800',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Format date for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  // Fetch email types for filter dropdown
  const fetchEmailTypes = useCallback(async () => {
    try {
      const response = await scheduledEmailTypeAPI.getAll();
      setEmailTypes(response.data.emailTypes || []);
    } catch (err) {
      console.error('Error fetching email types:', err);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const options = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        ...filters
      };

      // Remove empty filters
      Object.keys(options).forEach(key => {
        if (options[key] === '' || options[key] === undefined) {
          delete options[key];
        }
      });

      const response = await scheduledEmailTypeAPI.getAllNotifications(options);

      setNotifications(response.data.notifications || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 0
      }));
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, sortBy, sortOrder, filters]);

  // Initial load
  useEffect(() => {
    fetchEmailTypes();
  }, [fetchEmailTypes]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle sort changes
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Skip notification
  const handleSkip = async (notification) => {
    try {
      setActionLoading(notification._id);
      await scheduledEmailTypeAPI.updateNotification(notification._id, {
        status: 'skipped',
        'manualOverride.skipped': true,
        'manualOverride.skippedBy': 'admin',
        'manualOverride.skippedReason': 'Manually skipped from notification list',
        'manualOverride.skippedAt': new Date().toISOString()
      });
      await fetchNotifications();
    } catch (err) {
      console.error('Error skipping notification:', err);
      setError(err.message || 'Failed to skip notification');
    } finally {
      setActionLoading(null);
    }
  };

  // Unskip notification
  const handleUnskip = async (notification) => {
    try {
      setActionLoading(notification._id);
      await scheduledEmailTypeAPI.updateNotification(notification._id, {
        status: 'pending',
        'manualOverride.skipped': false,
        'manualOverride.skippedBy': null,
        'manualOverride.skippedReason': null,
        'manualOverride.skippedAt': null
      });
      await fetchNotifications();
    } catch (err) {
      console.error('Error unskipping notification:', err);
      setError(err.message || 'Failed to unskip notification');
    } finally {
      setActionLoading(null);
    }
  };

  // Delete notification
  const handleDelete = async (notification) => {
    if (!window.confirm(`Are you sure you want to delete this notification for "${notification.eventName}"?`)) {
      return;
    }
    try {
      setActionLoading(notification._id);
      await scheduledEmailTypeAPI.deleteNotification(notification._id);
      await fetchNotifications();
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError(err.message || 'Failed to delete notification');
    } finally {
      setActionLoading(null);
    }
  };

  // Open edit modal
  const openEditModal = (notification) => {
    setEditForm({
      scheduledFor: formatDateForInput(notification.scheduledFor),
      subject: notification.emailDetails?.subject || '',
      notes: notification.notes || ''
    });
    setEditModal({ open: true, notification });
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditModal({ open: false, notification: null });
    setEditForm({});
  };

  // Save edit
  const handleSaveEdit = async () => {
    try {
      setActionLoading(editModal.notification._id);

      const updateData = {
        scheduledFor: new Date(editForm.scheduledFor).toISOString(),
        'emailDetails.subject': editForm.subject,
        notes: editForm.notes,
        hasOverride: true,
        overriddenAt: new Date().toISOString(),
        overriddenBy: 'admin'
      };

      await scheduledEmailTypeAPI.updateNotification(editModal.notification._id, updateData);
      closeEditModal();
      await fetchNotifications();
    } catch (err) {
      console.error('Error updating notification:', err);
      setError(err.message || 'Failed to update notification');
    } finally {
      setActionLoading(null);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: '',
      emailTypeId: '',
      fromDate: '',
      toDate: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Get email type name
  const getEmailTypeName = (notification) => {
    if (notification.emailTypeId) {
      const type = emailTypes.find(t => t._id === notification.emailTypeId);
      return type?.name || notification.emailTypeCode || 'Unknown Type';
    }
    return notification.notificationType || 'Legacy Type';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/scheduled-emails')}
                className="text-slate-600 hover:text-slate-900"
              >
                ← Back
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <span>📋</span>
                  All Scheduled Notifications
                </h1>
                <p className="text-sm text-slate-600">
                  Full database view with editing capabilities
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-white">
                {pagination.total} Total
              </Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchNotifications}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="py-4">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Filters</span>
              {Object.values(filters).some(v => v) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Clear All
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                  <option value="skipped">Skipped</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Email Type Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Type</label>
                <select
                  value={filters.emailTypeId}
                  onChange={(e) => handleFilterChange('emailTypeId', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                >
                  <option value="">All Types</option>
                  {emailTypes.map(type => (
                    <option key={type._id} value={type._id}>{type.name}</option>
                  ))}
                </select>
              </div>

              {/* From Date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>

              {/* To Date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>

              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Event name, subject..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-6">
            {error}
            <button
              onClick={() => setError(null)}
              className="float-right font-bold"
            >×</button>
          </div>
        )}

        {/* Notifications Table */}
        <Card>
          <CardContent className="p-0">
            {loading && notifications.length === 0 ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                <p className="text-slate-500">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-slate-500">No notifications found matching your filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('scheduledFor')}
                      >
                        Scheduled For {sortBy === 'scheduledFor' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('eventName')}
                      >
                        Event {sortBy === 'eventName' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                        Type
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('status')}
                      >
                        Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                        Subject
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                        Recipients
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {notifications.map((notification) => (
                      <tr key={notification._id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-900">
                            {formatDate(notification.scheduledFor)}
                          </div>
                          {notification.hasOverride && (
                            <Badge variant="outline" className="text-xs mt-1 bg-amber-50 text-amber-700 border-amber-200">
                              Modified
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-900">
                            {notification.eventName || 'Unknown Event'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {notification.eventDate && formatDate(notification.eventDate).split(',')[0]}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-600">
                            {getEmailTypeName(notification)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={getStatusColor(notification.status)}>
                            {notification.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-600 max-w-xs truncate">
                            {notification.emailDetails?.subject || 'No subject'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-600">
                            {notification.recipientCount || notification.recipients?.length || 0}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {notification.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditModal(notification)}
                                  disabled={actionLoading === notification._id}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSkip(notification)}
                                  disabled={actionLoading === notification._id}
                                  className="text-amber-600 hover:text-amber-700"
                                >
                                  Skip
                                </Button>
                              </>
                            )}
                            {notification.status === 'skipped' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnskip(notification)}
                                disabled={actionLoading === notification._id}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                Unskip
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(notification)}
                              disabled={actionLoading === notification._id}
                              className="text-red-600 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1 || loading}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </main>

      {/* Edit Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-slate-900">
                Edit Notification
              </h2>
              <p className="text-sm text-slate-600">
                {editModal.notification?.eventName}
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Scheduled Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={editForm.scheduledFor}
                  onChange={(e) => setEditForm(prev => ({ ...prev, scheduledFor: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subject Line
                </label>
                <input
                  type="text"
                  value={editForm.subject}
                  onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  placeholder="Optional notes about this notification..."
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-slate-50 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={closeEditModal}
                disabled={actionLoading === editModal.notification?._id}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={actionLoading === editModal.notification?._id}
              >
                {actionLoading === editModal.notification?._id ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationListPage;
